/**
 * How a location sanitizes, so the check matches reality (FRAMEWORK MP4). From the source protocols' product table:
 * mixed or dispensed sanitizer is tested with the MATCHING strip when mixed and every 4 hours (Food Code 4-302.14,
 * 4-501.114); ready-to-use no-rinse sprays need no strip ("use as labeled"). Brand names are examples named in the
 * protocols, not endorsements or regulatory approvals: any EPA-registered food-contact sanitizer used per its label works.
 */
export type SanitizerMethod = "mix" | "dispenser" | "ready_to_use";
export type SanitizerChemical = "quat" | "chlorine" | "iodine";

export interface SanitizerProduct {
  name: string; method: SanitizerMethod; chemical?: SanitizerChemical; strip?: string; note?: string;
  /** The catalog purposes (products.ts): what it is, and the strips that test it. */
  purpose: import("./products.ts").ProductPurposeId; stripPurpose?: import("./products.ts").ProductPurposeId;
}

export const SANITIZER_PRODUCTS: SanitizerProduct[] = [
  { purpose: "sanitizer_quat", stripPurpose: "strips_quat", name: "Steramine Sanitizing Tablets", method: "mix", chemical: "quat", strip: "Hydrion QT-40 quat test strips (0–400 ppm)", note: "Tablets: the easiest to mix right" },
  { purpose: "sanitizer_quat", stripPurpose: "strips_quat", name: "Ecolab Oasis 146 Multi-Quat", method: "dispenser", chemical: "quat", strip: "Hydrion QT-40 quat test strips (0–400 ppm)" },
  { purpose: "sanitizer_quat", stripPurpose: "strips_quat", name: "Kay-5 Sanitizer", method: "mix", chemical: "quat", strip: "Hydrion QT-40 quat test strips (0–400 ppm)" },
  { purpose: "sanitizer_chlorine", stripPurpose: "strips_chlorine", name: "Clorox Disinfecting Bleach", method: "mix", chemical: "chlorine", strip: "Hydrion chlorine test strips (0–200 ppm)", note: "EPA 5813-120. The label: 2 teaspoons per gallon (about 185 ppm), 2 minutes" },
  { purpose: "sanitizer_chlorine", stripPurpose: "strips_chlorine", name: "Clorox Germicidal Bleach (commercial)", method: "mix", chemical: "chlorine", strip: "Hydrion chlorine test strips (0–200 ppm)", note: "Or plain unscented bleach whose label has food-contact directions" },
  { purpose: "sanitizer_iodine", stripPurpose: "strips_iodine", name: "Iodophor sanitizer (e.g., BTF Iodophor)", method: "mix", chemical: "iodine", strip: "Hydrion iodine test strips" },
  { purpose: "sanitizer_spray", name: "PURELL Foodservice Surface Sanitizer", method: "ready_to_use", note: "EPA 84150-3. No strip needed; leave wet 60 seconds, no rinse" },
  { purpose: "sanitizer_spray", name: "CloroxPro Clorox Anywhere No Rinse Sanitizer", method: "ready_to_use", note: "EPA 67619-44. No strip needed; leave wet 1 minute, no rinse" },
  { purpose: "sanitizer_spray", name: "CloroxPro Clorox Anywhere Daily Disinfectant & Sanitizer", method: "ready_to_use", note: "EPA 67619-42. No strip needed; leave wet 1 minute, no rinse" },
];

export const SANITIZER_RANGE: Record<SanitizerChemical, string> = {
  chlorine: "Chlorine per your bleach label (Clorox Disinfecting Bleach: about 185 ppm, 2 minutes); never under 50 ppm",
  quat: "Quat per label, usually 200–400 ppm; leave wet for the label's time (Steramine: 1 minute)",
  iodine: "Iodine 12.5–25 ppm",
};

export const STRIP_FOR: Record<SanitizerChemical, string> = {
  quat: "quat test strips", chlorine: "chlorine test strips", iodine: "iodine test strips",
};

export interface SanitizerSetup { methods: SanitizerMethod[]; chemical?: SanitizerChemical; product?: string }

/** Does anything at this location need strength testing? Only mixed or dispensed sanitizer does. */
export function needsStripTest(s?: SanitizerSetup): boolean {
  return !s || s.methods.some((m) => m === "mix" || m === "dispenser");
}

/** Label and one-line hint for the opening and 4-hour sanitizer checks. */
export function sanitizerCheckText(s: SanitizerSetup | undefined, when: "open" | "recheck"): { label: string; hint: string } {
  if (!needsStripTest(s)) return {
    label: when === "open" ? "Check the sanitizer spray" : "Sanitizer spray still right",
    hint: "Right ready-to-use spray, not expired. Leave surfaces wet for the time on the label. No test strip needed.",
  };
  const chem = s?.chemical;
  const strip = chem ? STRIP_FOR[chem] : "the test strips that match your sanitizer";
  const range = chem ? SANITIZER_RANGE[chem] : "the strength on the label";
  const dispenser = s?.methods.includes("dispenser") && !s.methods.includes("mix");
  return {
    label: when === "open" ? (dispenser ? "Test sanitizer from the dispenser" : "Mix and test sanitizer") : "Retest sanitizer and cloth bucket",
    hint: `Dip ${strip}: ${range}. Weak? Add sanitizer. Strong? Add water. Retest.`,
  };
}
